// Global Instructions Rule Applied!
// Fund-level management-fee calculation basis for the Fee Booking workbench.
//   GET /fund-transactions/fund/:fundId/fee-basis   (fee__fund_basis)
//     -> FeeBasis: the "Fee basis" card for the selected period
//   GET /fund-transactions/fund/:fundId/fee-history (fee__booking_history)
//     -> FeeHistoryRow[]: one row per fee-period fund transaction, each with
//        its basis - the snapshot frozen at booking, or a live recompute on
//        today's terms ('recomputed') for transactions that predate snapshots.
import { z } from 'zod'
import { validateWithZodSchema } from '../../core/utils/utilities.ts'

export const FeeBasisSegmentSchema = z.object({
  segment_from: z.string(),
  segment_to: z.string(),
  days: z.number().nullish(),
  basis_label: z.string().nullish(),
  // headline (unscaled) annual rate; fee = fee_paying_basis x rate x year_fraction
  rate: z.number().nullish(),
  year_fraction: z.number().nullish(),
  fee_paying_basis: z.number().nullish(),
  fee: z.number().nullish(),
  commitment_count: z.number().nullish(),
  // accrues from a late closer's closing date, not the segment start
  is_late_closer: z.boolean().nullish()
})

const FeeBasisBucketSchema = z.object({
  amount: z.number(),
  count: z.number(),
  fee_paying_amount: z.number().nullish()
})

export const FeeBasisSchema = z.object({
  fund_id: z.string().nullish(),
  period_from: z.string().nullish(),
  period_to: z.string().nullish(),
  basis_label: z.string().nullish(),
  // Net Invested Capital only: 'book' (cost held as on the ledger) or 'cash'
  // (cost held less capitalised interest and non-cash basis); null otherwise
  basis_projection: z.string().nullish(),
  basis_amount: z.number().nullish(),
  committed_total: z.number().nullish(),
  nic_amount: z.number().nullish(),
  // 'Regulatory capital + assumed leverage' only: the RC breakdown as at period end
  regulatory_capital: z
    .object({
      as_of: z.string().nullish(),
      pool: z.number().nullish(),
      regulatory_capital: z.number().nullish(),
      institutional_capital: z.number().nullish(),
      institutional_count: z.number().nullish(),
      non_institutional_paid_in: z.number().nullish(),
      non_institutional_committed: z.number().nullish(),
      non_institutional_count: z.number().nullish(),
      government_committed: z.number().nullish(),
      government_counted: z.number().nullish(),
      government_count: z.number().nullish(),
      government_cap_applied: z.boolean().nullish(),
      uncategorised_count: z.number().nullish(),
      // side-letter rates cover the fee on Assumed Leverage (false = the
      // full rate for every LP); absent on snapshots taken before the base
      // was split by each LP's own Regulatory Capital
      side_letter_rate_on_leverage: z.boolean().nullish(),
      gearing_ratio: z.number().nullish(),
      assumed_leverage: z.number().nullish()
    })
    .nullish(),
  fee_paying_basis: z.number().nullish(),
  // rate-weighted: sum(basis x side-letter rate / headline rate) / basis
  fee_paying_share: z.number().nullish(),
  rate_at_period_end: z.number().nullish(),
  day_count_label: z.string().nullish(),
  frequency_label: z.string().nullish(),
  late_closer_policy_label: z.string().nullish(),
  period_fee: z.number().nullish(),
  has_late_closers: z.boolean().nullish(),
  buckets: z
    .object({
      full: FeeBasisBucketSchema,
      reduced: FeeBasisBucketSchema,
      non_fee_paying: FeeBasisBucketSchema
    })
    .nullish(),
  segments: z.array(FeeBasisSegmentSchema).nullish(),
  warning: z.string().nullish()
})

export const FeeHistoryRowSchema = z.object({
  fund_transactions__id: z.string(),
  fund_transactions__fee_period_start: z.string(),
  fund_transactions__fee_period_end: z.string(),
  fund_transactions__transaction_date: z.string().nullish(),
  transaction_statuses__label: z.string().nullish(),
  prog__amount: z.number().nullish(),
  prog__period_fee: z.number().nullish(),
  prog__true_up: z.number().nullish(),
  prog__basis_source: z.enum(['snapshot', 'recomputed']),
  // drift: today's period fee for the window vs the snapshot's (0 for
  // recomputed rows, null when the fund can no longer be priced)
  prog__current_period_fee: z.number().nullish(),
  prog__drift: z.number().nullish(),
  basis: FeeBasisSchema,
  // today's basis beside a snapshot; null on recomputed rows
  current_basis: FeeBasisSchema.nullish()
})

export type FeeBasisSegment = z.infer<typeof FeeBasisSegmentSchema>
export type FeeBasis = z.infer<typeof FeeBasisSchema>
export type FeeHistoryRow = z.infer<typeof FeeHistoryRowSchema>

export const validateFeeBasis = (data: unknown): FeeBasis => validateWithZodSchema(FeeBasisSchema, data, 'fee basis')

export const validateFeeHistory = (data: unknown): FeeHistoryRow[] =>
  validateWithZodSchema(FeeHistoryRowSchema, data, 'fee history', { isArray: true })
